import React, { useState, useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Icon,
  Spinner,
  Center,
  Box,
} from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

interface Categoria {
  seqCategoria: number;
  dscCategoria: string;
  datRegistro: string;
  numRegistro: number;
}

interface TabelaCategoriaProps {
  listaCategoria: Categoria[];
  listaVazia: string;
  loading: boolean;
}

type Ordenacao = { chave: keyof Categoria; direcao: 'asc' | 'desc' } | null;

const TabelaCategoria = ({ listaCategoria, listaVazia, loading }: TabelaCategoriaProps) => {
  const navigate = useNavigate();
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof Categoria | null>(null);

  const manipularOrdenacao = (chave: keyof Categoria) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaCategoria];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaCategoria, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof Categoria) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const renderizarLinhas = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((categoria) => (
        <Tr key={categoria.seqCategoria}>
          <Td>{categoria.seqCategoria}</Td>
          <Td isTruncated maxW="250px">{categoria.dscCategoria}</Td>
          <Td>{categoria.datRegistro.split('-').reverse().join('/')}</Td>
          <Td>{categoria.numRegistro === 1 ? 'Ativo' : 'Inativo'}</Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() => navigate(`editar-categoria/${categoria.seqCategoria}`)}
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={5} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              onClick={() => manipularOrdenacao('seqCategoria')}
              onMouseEnter={() => setColunaDestacada('seqCategoria')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              color={ordenacao?.chave === 'seqCategoria' || colunaDestacada === 'seqCategoria' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                ID {obterIndicadorOrdenacao('seqCategoria')}
              </Box>
            </Th>
            <Th
              onClick={() => manipularOrdenacao('dscCategoria')}
              onMouseEnter={() => setColunaDestacada('dscCategoria')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              color={ordenacao?.chave === 'dscCategoria' || colunaDestacada === 'dscCategoria' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                Descrição {obterIndicadorOrdenacao('dscCategoria')}
              </Box>
            </Th>
            <Th
              onClick={() => manipularOrdenacao('datRegistro')}
              onMouseEnter={() => setColunaDestacada('datRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              color={ordenacao?.chave === 'datRegistro' || colunaDestacada === 'datRegistro' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                Data de Registro {obterIndicadorOrdenacao('datRegistro')}
              </Box>
            </Th>
            <Th
              onClick={() => manipularOrdenacao('numRegistro')}
              onMouseEnter={() => setColunaDestacada('numRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              color={ordenacao?.chave === 'numRegistro' || colunaDestacada === 'numRegistro' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                Ativo {obterIndicadorOrdenacao('numRegistro')}
              </Box>
            </Th>
            <Th>Editar</Th>
          </Tr>
        </Thead>
        <Tbody>{renderizarLinhas()}</Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaCategoria;
